"use client";

import { Intercom, LiveChatLoaderProvider } from "react-live-chat-loader";
import type { ReactNode } from "react";

const INTERCOM_APP_ID = "o7y22sae";

// Industry-standard live-chat facade for the marketing route group.
//
// `react-live-chat-loader` (Calibre) is the library web.dev names in its
// "Best practices for using third-party embeds" article and the same
// pattern Postmark used to hit Lighthouse 100. It renders a static button
// styled like the Intercom launcher immediately on page load, then lazily
// loads the real Intercom widget when ANY of:
//
//   - the user clicks the fake button
//   - the user hovers (preconnects to widget.intercom.io for faster load)
//   - `requestIdleCallback` fires after `idlePeriod` ms
//
// It also skips loading on slow connections (`navigator.connection.effectiveType`)
// and when data-saver is enabled — both Lighthouse-friendly defaults we'd
// otherwise have to handle ourselves.
//
// Scoping: this provider is mounted only inside `app/(marketing)/layout.tsx`,
// so authenticated `(app)/` routes keep their existing secure-mode Intercom
// boot inside `<Main>`. Pages Router routes still use the eager `<Script>`
// in `pages/_document.tsx` — unchanged.
//
// `lib/intercom.ts:show()` keeps working as a universal fallback for the
// FAQ "live chat." link (which is rendered on both App Router and Pages
// Router surfaces). Both code paths end up calling `window.Intercom(...)`
// on the same script instance, so they coexist cleanly.
//
// `idlePeriod={2000}` matches the library default (load real widget after
// 2 s of browser idle if no interaction). Tune via this prop if needed.
export default function IntercomFacade({ children }: { children: ReactNode }) {
  return (
    <LiveChatLoaderProvider
      provider="intercom"
      providerKey={INTERCOM_APP_ID}
      idlePeriod={2000}
    >
      {children}
      <Intercom />
    </LiveChatLoaderProvider>
  );
}
